const authorization = {
  // Page titles
  pageTitle: 'Platform Authorization',
  pageSubtitle: 'Authorize Safaqat 24 platform to market your property',

  // Stepper steps
  steps: {
    consent: 'Consent',
    authType: 'Authorization Type',
    identity: 'Identity Verification',
    ownership: 'Ownership Proof',
    property: 'Property Details',
    details: 'Property Details',
    contact: 'Contact',
    declarations: 'Declarations',
    signature: 'Signature',
  },

  // Step 1: Consent
  consent: {
    title: 'Authorization Consent',
    subtitle: 'Please read and agree to the terms to proceed',
    agreement: 'I confirm that I am the property owner or an authorized representative, and I authorize Safaqat 24 Platform to market and advertise my property according to the specified terms and conditions.',
    termsTitle: 'Authorization Terms and Conditions',
    terms: [
      'The platform commits to displaying the property according to information provided by the owner',
      'The platform may make editorial adjustments to improve listing quality',
      'The platform commits to not sharing owner data with third parties without permission',
      'The owner can cancel the authorization at any time',
      'Agreed platform commission applies upon successful transaction',
    ],
    acceptTerms: 'I agree to the terms and conditions',
    acceptRequired: 'You must agree to the terms to proceed',
  },

  // Step 2: Authorization Type
  authType: {
    title: 'Authorization Type',
    subtitle: 'Choose the authorization type that suits your needs',
    types: {
      adOnly: {
        title: 'Listing Only',
        description: 'Authorize the platform to publish your property listing only',
        features: ['Publish listing', 'Respond to inquiries', 'Update information'],
      },
      adMarketing: {
        title: 'Listing + Marketing',
        description: 'Comprehensive authorization for listing and active marketing',
        features: ['All listing features', 'Active marketing', 'Featured ads', 'Regular reports'],
      },
      fullService: {
        title: 'Full Service',
        description: 'Complete authorization including negotiation and closing',
        features: ['All marketing features', 'Negotiate on your behalf', 'Arrange viewings', 'Close deals'],
      },
    },
    recommended: 'Recommended',
  },

  // Step 3: Identity Verification
  identity: {
    title: 'Identity Verification',
    subtitle: 'Enter your 10-digit national ID or Iqama number. A push will be sent to the Nafath app on your phone.',
    nafathRequired: 'Nafath verification is required to complete authorization',
    comingSoon: 'Coming Soon',
    comingSoonDesc: 'Nafath verification will be available soon. You can proceed now and we will contact you to complete verification.',
    softGateNote: 'Nafath verification is not enabled yet. You can continue submitting your property and we will request verification later.',
    nationalIdLabel: 'National ID',
    nationalIdPlaceholder: '1xxxxxxxxx',
    verifying: 'Sending...',
    verifyCta: 'Start Nafath verification',
    skipForNow: 'Skip for now',
    continueWithNafath: 'Verify with Nafath',
  },

  // Step 4: Ownership Proof
  ownership: {
    title: 'Ownership Proof',
    subtitle: 'Enter deed or ownership document details',
    deedType: 'Document Type',
    deedTypes: {
      deed: 'Property Deed',
      contract: 'Unified Rental Contract',
      powerOfAttorney: 'Power of Attorney',
    },
    deedNumber: 'Deed Number',
    deedNumberPlaceholder: 'Enter deed number',
    deedDate: 'Deed Date',
    ownerIdNumber: 'Owner ID Number',
    ownerIdNumberPlaceholder: 'Enter ID number',
    uploadDeed: 'Upload Deed Image',
    uploadDeedDescription: 'Clear image of the deed or document (optional)',
    dragDrop: 'Drag file here or',
    browse: 'Browse',
    maxSize: 'Max size: 10MB',
    acceptedFormats: 'Accepted formats: PDF, JPG, PNG',
    uploadError: 'Could not upload the ownership document. Please try again.',
  },

  // Step 5: Property Details
  property: {
    title: 'Property Details',
    subtitle: 'Enter information about the property to be authorized',
    propertyType: 'Property Type',
    propertyTypes: {
      apartment: 'Apartment',
      villa: 'Villa',
      land: 'Land',
      building: 'Building',
      office: 'Office',
      shop: 'Shop',
    },
    listingType: 'Listing Type',
    listingTypes: {
      sale: 'Sale',
      rent: 'Rent',
    },
    city: 'City',
    district: 'District',
    address: 'Detailed Address',
    addressPlaceholder: 'Example: King Fahd Road, next to...',
    price: 'Asking Price',
    pricePlaceholder: 'Enter price',
    negotiable: 'Price is negotiable',
    area: 'Area (sqm)',
    areaPlaceholder: 'Enter area',
    rooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    description: 'Property Description',
    descriptionPlaceholder: 'Write a detailed description of the property...',
    photos: 'Property Photos',
    photosDescription: 'Add clear photos of the property (at least 5 recommended)',
  },

  // Step 6: Contact Preferences
  contact: {
    title: 'Contact Preferences',
    subtitle: 'How do you want to handle inquiries?',
    // Shown for FAL-managed (platform-licensed) listings instead of letting the
    // owner choose a handler — Safaqat24 is the sole point of contact.
    falManaged: {
      title: 'Safaqat24 handles all inquiries',
      body: 'Because this listing is published under Safaqat24’s FAL license, interested buyers and renters contact Safaqat24 directly - not you. Our team manages every inquiry and keeps you updated with any offers or important messages.',
    },
    inquiryHandler: 'Who receives inquiries?',
    handlers: {
      platform: 'Platform receives and responds',
      owner: 'I receive inquiries directly',
      both: 'Platform filters and forwards important ones',
    },
    ownerPhone: 'Owner Phone Number',
    ownerPhonePlaceholder: '05xxxxxxxx',
    ownerEmail: 'Email Address',
    ownerEmailPlaceholder: 'example@email.com',
    preferredTime: 'Preferred Contact Times',
    times: {
      anytime: 'Anytime',
      morning: 'Morning (8AM - 12PM)',
      afternoon: 'Afternoon (12PM - 5PM)',
      evening: 'Evening (5PM - 9PM)',
    },
    showPhone: 'Show phone number in listing',
    showPhoneDescription: 'Your number will be visible to interested parties',
  },

  // Step 7: Legal Declarations
  declarations: {
    title: 'Legal Declarations',
    subtitle: 'Please read and agree to the following declarations',
    items: [
      {
        id: 'ownership',
        title: 'Ownership Declaration',
        text: 'I declare that I am the legal owner of the property or legally authorized to act on its behalf',
      },
      {
        id: 'accuracy',
        title: 'Information Accuracy',
        text: 'I declare that all information provided is true and accurate',
      },
      {
        id: 'disputes',
        title: 'Free from Disputes',
        text: 'I declare that the property is free from any legal disputes or liens',
      },
      {
        id: 'commission',
        title: 'Commission Agreement',
        text: 'I agree to the specified commission rate if the deal is completed through the platform',
      },
    ],
    acceptAll: 'Accept all declarations',
  },

  // Step 8: Signature
  signature: {
    title: 'Electronic Signature',
    subtitle: 'Sign the authorization electronically',
    absherRequired: 'Absher signature is required to complete authorization',
    comingSoon: 'Coming Soon',
    comingSoonDesc: 'Electronic signature through Absher will be available soon. You can proceed now and we will contact you to complete the signature.',
    skipForNow: 'Submit without electronic signature',
    signWithAbsher: 'Sign with Absher',
    manualSignature: 'Manual Signature',
    manualSignatureDesc: 'Draw your signature in the box below',
    canvasHint: 'Use your mouse or finger to sign',
    clear: 'Clear',
    agreementText: 'By signing below, I confirm my agreement to all terms and conditions and the accuracy of the information provided.',
  },

  // Success
  success: {
    title: 'Authorization Request Received',
    subtitle: 'We will review your request and contact you within 24 hours',
    referenceNumber: 'Reference Number',
    whatNext: 'What happens next',
    steps: [
      'Request review by our team',
      'Contact you to confirm details',
      'Publish listing upon approval',
    ],
    viewStatus: 'Track Request Status',
    submitAnother: 'Authorize Another Property',
    backToHome: 'Back to Home',
  },

  // Actions
  actions: {
    next: 'Next',
    back: 'Back',
    submit: 'Submit Authorization',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    save: 'Save as Draft',
  },

  // Validation
  validation: {
    consentRequired: 'You must agree to the terms',
    authTypeRequired: 'Please select an authorization type',
    deedRequired: 'Please enter deed information',
    propertyRequired: 'Please enter property details',
    contactRequired: 'Please enter contact information',
    declarationsRequired: 'Please accept all declarations',
  },
};

export default authorization;
